import type { Auftrag } from '@awf/shared';
import { type ReactNode, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { klasse } from '../ui/klasse.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { KiHinweis } from '../ui/ki-hinweis.js';
import { Seitenkopf } from '../ui/seitenkopf.js';
import { Statusmarke, TON_JE_GRUPPE } from '../ui/statusmarke.js';
import { ErrorState, LoadingState } from '../ui/states.js';
import { useKonfetti } from '../ui/use-konfetti.js';
import { alsBildquelle } from './bildquelle.js';
import { AnalyseSeitenListe } from './analyse-seiten-liste.js';
import { BildLupeProvider, istHttps, LupenKnopf } from './bild-lupe.js';
import { driveDownload, wordDownload } from './download-adresse.js';
import { KeinBedarfKarte } from './kein-bedarf-karte.js';
import stile from './ergebnis-page.module.css';
import type { AuftragsQuelle } from './runs-source.js';
import { gruppeVon, STATUS_TEXT, TYP_TEXT } from './status.js';
import { useAuftrag } from './use-runs.js';
import type { StarteVolleAnalyse } from './volle-analyse-knopf.js';
import { VorschauRahmen } from './vorschau-rahmen.js';

/**
 * Das Ergebnis eines Auftrags auf EINER Seite wie die Ergebnisansicht der Content-Factory
 * (ergebnis-ansicht.tsx, Rueckmeldung 2026-10-13): oben Titel und Status, dann "Downloads" mit
 * Word-Dokument und Bildern, darunter "Vorschau" in einer Karte. Erreichbar ueber "Ergebnis
 * oeffnen" an der Beitragskarte; vor dem Abschluss zeigt sie den Zwischenstand.
 */

/** Ob `Verweise` etwas zu zeigen haette -- sonst stuende eine leere Knopfzeile da. */
export function hatVerweise(auftrag: Auftrag): boolean {
  const ergebnis = auftrag.ergebnis;
  return Boolean(ergebnis?.vorschauUrl ?? ergebnis?.docxUrl ?? ergebnis?.driveOrdnerUrl);
}

export function hatBilder(auftrag: Auftrag): boolean {
  return (auftrag.ergebnis?.bildUrls?.length ?? 0) > 0;
}

/**
 * Eine Kachel je Datei wie die Downloads der Content-Factory (ergebnis-ansicht.module.css):
 * Name links, Symbol rechts, die ganze Kachel ist der Verweis. Extern, deshalb neuer Tab.
 */
function DateiKachel({ text, aria, href }: { text: string; aria: string; href: string }) {
  return (
    <li>
      {/* Kein `download`-Attribut: es wirkt nur auf derselben Herkunft. Den Download loest
          Google selbst aus (Content-Disposition), die Seite bleibt dabei stehen. */}
      <a className={stile.datei} href={href} aria-label={aria} rel="noreferrer">
        <span className={stile.dateiname}>{text}</span>
        <span className={stile.dateiSymbol}>
          <LinienSymbol name="download" groesse={18} />
        </span>
      </a>
    </li>
  );
}

function wordVon(auftrag: Auftrag): string | null {
  const docx = auftrag.ergebnis?.docxUrl;
  return docx ? wordDownload(docx) : null;
}

/**
 * Drive-Bilder direkt als Datei; jede andere https-Adresse (etwa die des Bildgenerators, die die
 * Bilder-Kette bei `fertig` behaelt) als Verweis auf das Bild selbst. Ohne diesen Rueckfall bot
 * ein fertiger Bilder-Auftrag gar keinen Download an (Rueckmeldung 2026-10-13).
 */
function bildDownload(url: string): string | null {
  return driveDownload(url) ?? (istHttps(url) ? url : null);
}

function Downloads({ auftrag }: { auftrag: Auftrag }) {
  const word = wordVon(auftrag);
  // Vor dem Abschluss zeigen die Bild-URLs auf den Generator: nichts zum Herunterladen.
  const bilder =
    auftrag.status === 'fertig'
      ? (auftrag.ergebnis?.bildUrls ?? []).flatMap((url, i) => {
          const href = bildDownload(url);
          return href ? [{ url, href, nummer: i + 1 }] : [];
        })
      : [];
  if (!word && bilder.length === 0) return null;
  return (
    <section className={stile.abschnitt} aria-labelledby="downloads-titel">
      <h2 id="downloads-titel" className={stile.abschnittTitel}>
        Downloads
      </h2>
      {word && (
        <div className={stile.gruppe}>
          <h3 className={stile.gruppenTitel}>Word-Dokument</h3>
          <ul className={stile.dateien}>
            <DateiKachel
              text={TYP_TEXT[auftrag.typ]}
              aria={`${TYP_TEXT[auftrag.typ]} als Word-Dokument herunterladen`}
              href={word}
            />
          </ul>
        </div>
      )}
      {bilder.length > 0 && (
        <div className={stile.gruppe}>
          <h3 className={stile.gruppenTitel}>Bilder</h3>
          <ul className={stile.dateien}>
            {bilder.map((b) => (
              <DateiKachel
                key={b.url}
                text={`Bild ${b.nummer}`}
                aria={`Bild ${b.nummer} herunterladen`}
                href={b.href}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Das Bild mit dem Vergroessern-Zeichen unten rechts, wie das Beitragsbild der Content-Factory
 * (artikel-vorschau.tsx dort, Rueckmeldung 2026-10-13): Der Dialog zeigt es gross, kein neuer
 * Tab. Die Seite zeigt die Adresse schon selbst (auch die des Bildgenerators), darum
 * `schonGezeigt`; nur eine Adresse ohne https bekommt kein Zeichen (Codex-Gate PR #70).
 */
function GrossesBild({
  url,
  nummer,
  anzahl,
  children,
}: {
  url: string;
  nummer: number;
  anzahl: number;
  children: ReactNode;
}) {
  const quelle = alsBildquelle(url);
  return (
    <div className={stile.bildRahmen}>
      {children}
      {istHttps(quelle) && (
        <LupenKnopf
          name={`Bild ${nummer}`}
          vorschau={quelle}
          titel={`Bild ${nummer} von ${anzahl}`}
          schonGezeigt
          className={stile.bildLupe}
        />
      )}
    </div>
  );
}

function Bilder({ auftrag }: { auftrag: Auftrag }) {
  // Nach URL gemerkt, nicht nach Position: Checkpoint 7 tauscht die kurzlebigen
  // Generator-Links gegen dauerhafte Drive-Links. Eine neue URL ist damit von selbst wieder
  // unverdaechtig, ohne dass hier irgendwo zurueckgesetzt werden muesste.
  const [kaputt, setKaputt] = useState<ReadonlySet<string>>(() => new Set());
  const bilder = auftrag.ergebnis?.bildUrls ?? [];
  if (bilder.length === 0) return null;

  // Zwischen Freigabe und Fertigstellung zeigen die Bild-URLs auf den Generator und koennen
  // kurzlebig sein. Erst Checkpoint 7 ersetzt sie durch dauerhafte Drive-Links.
  const vorlaeufig = auftrag.status !== 'fertig';

  return (
    <>
      {vorlaeufig && (
        <p className="eds-text-secondary">
          Diese Vorschauen sind vorläufig und können nach kurzer Zeit nicht mehr laden. Die
          endgültigen Bilder stehen nach Abschluss hier zum Herunterladen bereit.
        </p>
      )}
      <div className={stile.bilder}>
        {/* Der Hinweis ersetzt das Bild ueber den Zustand, NICHT ueber `replaceWith()`
            am DOM-Knoten. React verwaltet dieses `<img>`: wird es unter React weg aus
            dem Baum genommen, zeigt Reacts Verweis weiter auf ein Element, das nicht
            mehr dort haengt -- der naechste Firestore-Snapshot mit anderen Bild-URLs
            bricht dann beim Aufraeumen mit "The node to be removed is not a child of
            this node" ab und reisst die ganze Seite mit (belegt im Test). */}
        {bilder.map((url, i) =>
          kaputt.has(url) ? (
            <p key={url} className="eds-text-tertiary">
              Bild {i + 1} ist nicht mehr abrufbar.
            </p>
          ) : (
            <GrossesBild key={url} url={url} nummer={i + 1} anzahl={bilder.length}>
              <img
                // Nur die Quelle wird umgeschrieben (Drive-Seite -> Vorschaubild, siehe
                // bildquelle.ts). `key` und `kaputt` bleiben an der Original-URL.
                src={alsBildquelle(url)}
                alt={`Bild ${i + 1} des Laufs`}
                loading="lazy"
                onError={() =>
                  setKaputt((bisher) => (bisher.has(url) ? bisher : new Set(bisher).add(url)))
                }
              />
            </GrossesBild>
          ),
        )}
      </div>
    </>
  );
}

/**
 * Die Vorschau wie dort unter den Downloads: beim Ratgeber die gerenderte Seite (`vorschauUrl`
 * ist nur das Kennzeichen; der Wert zeigt auf einen n8n-Webhook und wird NICHT verlinkt,
 * Architekturregel 1), bei Bildern die Bilder selbst.
 */
function Vorschau({
  auftrag,
  holenVorschau,
}: {
  auftrag: Auftrag;
  holenVorschau: (runId: string) => Promise<string>;
}) {
  const mitSeite = Boolean(auftrag.ergebnis?.vorschauUrl);
  if (!mitSeite && !hatBilder(auftrag)) return null;
  return (
    <section className={stile.abschnitt} aria-labelledby="vorschau-titel">
      <h2 id="vorschau-titel" className={stile.abschnittTitel}>
        Vorschau
      </h2>
      <div className={klasse(stile.vorschauKarte, !mitSeite && stile.nurBilder)}>
        {mitSeite ? (
          <VorschauRahmen runId={auftrag.runId} holen={holenVorschau} />
        ) : (
          <BildLupeProvider>
            <Bilder auftrag={auftrag} />
          </BildLupeProvider>
        )}
      </div>
    </section>
  );
}

function keinBedarf(auftrag: Auftrag): boolean {
  return auftrag.typ === 'pdp' && auftrag.befund?.keinBedarf === true;
}

/** Nur ein Abschluss mit echtem Ergebnis wird gefeiert, nie ein leerer oder vorlaeufiger. */
export function verdientKonfetti(auftrag: Auftrag): boolean {
  return (
    auftrag.status === 'fertig' &&
    !keinBedarf(auftrag) &&
    (wordVon(auftrag) !== null || hatBilder(auftrag))
  );
}

function Inhalt({
  auftrag,
  quelle,
  holenVorschau,
  starteVolleAnalyse,
}: {
  auftrag: Auftrag;
  quelle: AuftragsQuelle;
  holenVorschau: (runId: string) => Promise<string>;
  starteVolleAnalyse: StarteVolleAnalyse;
}) {
  if (keinBedarf(auftrag)) {
    return (
      <section className={stile.karte}>
        <KeinBedarfKarte auftrag={auftrag} quelle={quelle} starte={starteVolleAnalyse} />
      </section>
    );
  }
  if (auftrag.typ === 'analyse') {
    // Das Ergebnis einer Analyse IST die Seitenliste (Rueckmeldung 2026-10-13: "nicht im
    // Frontend"). Altlaeufe ohne Liste stammen aus der Zeit, als n8n es per Mail schickte.
    return (
      <section className={stile.karte}>
        {auftrag.analyseBefund ? (
          <AnalyseSeitenListe auftrag={auftrag} quelle={quelle} starte={starteVolleAnalyse} />
        ) : (
          <p className="eds-text-secondary">
            Diese Analyse lief vor der Anzeige im Frontend. Ihr Ergebnis wurde damals per E-Mail
            verschickt.
          </p>
        )}
      </section>
    );
  }
  if (!wordVon(auftrag) && !hatBilder(auftrag) && !auftrag.ergebnis?.vorschauUrl) {
    return (
      <section className={stile.karte}>
        <p className="eds-text-secondary">Kein Ergebnis hinterlegt.</p>
      </section>
    );
  }
  return (
    <>
      <Downloads auftrag={auftrag} />
      <Vorschau auftrag={auftrag} holenVorschau={holenVorschau} />
    </>
  );
}

export function ErgebnisPage({
  quelle,
  holenVorschau,
  starteVolleAnalyse,
}: {
  quelle: AuftragsQuelle;
  holenVorschau: (runId: string) => Promise<string>;
  starteVolleAnalyse: StarteVolleAnalyse;
}) {
  const { runId = '' } = useParams();
  const zustand = useAuftrag(quelle, runId);
  const pfad = [
    { text: 'Übersicht', ziel: '/' },
    { text: 'Fortschritt Ihres Auftrags', ziel: `/auftraege/${encodeURIComponent(runId)}` },
  ];

  if (zustand.art === 'laedt') {
    return (
      <>
        <Seitenkopf titel="Ergebnis" pfad={pfad} />
        <LoadingState text="Ergebnis wird geladen …" />
      </>
    );
  }
  if (zustand.art === 'fehler') {
    return (
      <>
        <Seitenkopf titel="Ergebnis" pfad={pfad} />
        <ErrorState text={zustand.text} onWiederholen={zustand.wiederholen} />
      </>
    );
  }
  const auftrag = zustand.auftrag;
  if (!auftrag) {
    return (
      <>
        <Seitenkopf titel="Auftrag nicht gefunden" pfad={[{ text: 'Übersicht', ziel: '/' }]} />
        <p>Diesen Auftrag gibt es nicht, oder er gehört zu einem anderen Konto.</p>
        <Link className="eds-button eds-button--primary" to="/">
          Zur Übersicht
        </Link>
      </>
    );
  }
  // Ein Ergebnis gibt es erst, wenn der Auftrag fertig ist. Vorher fuehrt die Adresse zum
  // Fortschritt: Eine Zwischenansicht mit vorlaeufigen Bildern irritierte (Rueckmeldung
  // 2026-10-13), freigegeben wird auf der Freigabeseite.
  if (gruppeVon(auftrag.status) !== 'fertig') {
    return <Navigate to={`/auftraege/${encodeURIComponent(auftrag.runId)}`} replace />;
  }
  return (
    <ErgebnisAnsicht
      auftrag={auftrag}
      quelle={quelle}
      pfad={pfad}
      holenVorschau={holenVorschau}
      starteVolleAnalyse={starteVolleAnalyse}
    />
  );
}

function ErgebnisAnsicht({
  auftrag,
  quelle,
  pfad,
  holenVorschau,
  starteVolleAnalyse,
}: {
  auftrag: Auftrag;
  quelle: AuftragsQuelle;
  pfad: readonly { text: string; ziel: string }[];
  holenVorschau: (runId: string) => Promise<string>;
  starteVolleAnalyse: StarteVolleAnalyse;
}) {
  useKonfetti(verdientKonfetti(auftrag));
  return (
    <>
      <Seitenkopf titel="Ergebnis" pfad={pfad} />
      <div className={stile.seite}>
        <KiHinweis className={stile.kiHinweis} />
        <div className={stile.kurztitel}>
          <h2 className={stile.kurztitelText}>
            {TYP_TEXT[auftrag.typ]}: {auftrag.thema}
          </h2>
          <Statusmarke ton={TON_JE_GRUPPE[gruppeVon(auftrag.status)]}>
            {STATUS_TEXT[auftrag.status]}
          </Statusmarke>
        </div>
        <Inhalt
          auftrag={auftrag}
          quelle={quelle}
          holenVorschau={holenVorschau}
          starteVolleAnalyse={starteVolleAnalyse}
        />
      </div>
    </>
  );
}
